import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import TicketsScannerSearchAutocompleteItem from './SearchAutocompleteItem'
import { forceSearch, updateQuery as updateQueryAction, resetOwnersList, selectQuery, selectOwnersError } from '../../stores/ticketsScanner/actions'

const TicketsScannerSearchAutocomplete = forwardRef((props, ref) => {
  const [width, setWidth] = useState("0")
  const [scrollPosition, setScrollPosition] = useState(-1)
  const RESULTS_LIMIT = 5
  const containerRef = useRef(null)
  
  const query = useSelector(selectQuery)
  const searchError = useSelector(selectOwnersError)
  const dispatch = useDispatch()
  const { owners, onSelect } = props

  useEffect(() => {
    updateWidth()
  }, [])

  // jQuery's outerWidth() is the border-box width, and so is getBoundingClientRect().width
  // (Bootstrap sets border-box globally). `:scope > input` is siblings("input"): a direct child
  // of this element's parent.
  const updateWidth = () => {
    const input = containerRef.current?.parentElement?.querySelector(":scope > input")
    if (input) setWidth(input.getBoundingClientRect().width)
  }

  const handleShowAllClick = () => {
    dispatch(forceSearch(query))
    // forceSearch opens the extended list and unmounts this row. Handing focus back to the search
    // input first gives the modal a real element to return focus to when it closes -- otherwise it
    // would capture the document body and strand anyone who got here with the keyboard.
    if (onSelect) onSelect()
  }

  const handleShowAllKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      handleShowAllClick()
    }
  }

  // SEARCH_OWNERS_FAILURE leaves `owners` holding the previous search's results, so everything
  // that reads the list has to know the visible state is the failure row, not those rows.
  const hasMore = () => {
    return !searchError && owners.totalCount > RESULTS_LIMIT
  }

  const listLength = () => {
    let length = owners.list.length
    if (hasMore()) {
      ++length
    }
    return length - 1
  }

  const scrollToPrev = () => {
    let position = scrollPosition - 1
    if (position < 0) {
      position = listLength()
    }
    setScrollPosition(position)
  }

  const scrollToNext = () => {
    let position = scrollPosition + 1
    if (position > listLength()) {
      position = 0
    }
    setScrollPosition(position)
  }

  const touchUnderScroll = () => {
    // The only row on screen is the failure row, and the stale list behind it is not selectable.
    if (searchError) return

    if (hasMore() && scrollPosition === listLength()) {
      dispatch(forceSearch(query))
      return
    }

    // There is a row to arrow onto even when there is no result under it -- "nobody found" after
    // an empty search -- so Enter can land here with nothing selected. Pre-existing.
    const owner = owners.list[scrollPosition]
    if (!owner) return

    dispatch(updateQueryAction(owner.barcode))
    dispatch(resetOwnersList())
  }

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    scrollToPrev,
    scrollToNext,
    touchUnderScroll
  }))

  let list = []

  if (searchError) {
    // $.ajax routed a failed search through application.js's global ajaxError flash. fetch does
    // not, and the failure was already being dispatched into state that nothing rendered -- so
    // without this the scanner would claim "nobody found" when the server is unreachable.
    list = (
      <div className="autocomplete-item disabled">
        <div className="row">
          <div className="col-xs-12">
            <h2 className="text-center text-danger">
              {I18n.t("tickets_scanner.errors.search_failed")}
            </h2>
          </div>
        </div>
      </div>
    )
  } else if (owners.list.length > 0) {
    list = owners.list.map((owner, i) => (
      <TicketsScannerSearchAutocompleteItem
        className={true ? "active" : null}
        key={owner.barcode}
        barcode={owner.barcode}
        name={owner.name}
        email={owner.email}
        highlight={i === scrollPosition}
        onSelect={onSelect}
      />
    ))
  } else {
    list = (
      <div className="autocomplete-item disabled">
        <div className="row">
          <div className="col-xs-12">
            <h2 className="text-center">
              {I18n.t("tickets_scanner.owners_list.not_found")}
            </h2>
          </div>
        </div>
      </div>
    )
  }

  let showMoreButton = null

  if (hasMore()) {
    let className = "autocomplete-item"
    if (scrollPosition === listLength()) {
      className += " active"
    }

    showMoreButton = (
      <div
        className={className}
        onClick={handleShowAllClick}
        onKeyDown={handleShowAllKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className="row">
          <div className="col-xs-12">
            <h4 className="text-center">
              {I18n.t("tickets_scanner.show_all_results", {
                count: owners.totalCount,
              })}
            </h4>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="members-autocomplete"
      style={{ width: width + "px" }}
    >
      {list}
      {showMoreButton}
    </div>
  )
})

export default TicketsScannerSearchAutocomplete
